import { useEffect, useRef } from "react";
import config from "../config";
import { getToken } from "../utils/storageUtils";
import type { QueueEntryApi } from "../services/queue/types";

/**
 * Abonnement temps réel à la file du médecin connecté (SSE, DEC-5).
 *
 * <p>On n'utilise pas l'API native {@code EventSource} car elle ne permet pas
 * d'envoyer l'en-tête {@code Authorization: Bearer} (auth JWT du projet). On
 * lit donc le flux {@code text/event-stream} via {@code fetch} + ReadableStream,
 * ce qui autorise les en-têtes. À chaque événement {@code queue}, on appelle
 * {@code onQueue} avec la file complète recalculée poussée par le serveur.
 *
 * <p>Reconnexion automatique avec back-off exponentiel (max 15 s). L'abonnement
 * est annulé proprement au démontage.
 *
 * @param onQueue  callback recevant la liste à jour (forme API à plat)
 * @param enabled  active/désactive l'abonnement (ex. seulement pour un médecin)
 */
export function useQueueStream(
  onQueue: (queue: QueueEntryApi[]) => void,
  enabled: boolean = true,
  streamPath: string = "/queue/doctor/me/stream"
): void {
  const onQueueRef = useRef(onQueue);
  onQueueRef.current = onQueue;

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();
    let stopped = false;
    let retryDelayMs = 1000;

    const dispatchEvent = (rawEvent: string) => {
      const dataPayload = rawEvent
        .split("\n")
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).replace(/^\s/, "").replace(/\r$/, ""))
        .join("\n")
        .trim();
      if (!dataPayload) return; // heartbeat / commentaire
      try {
        onQueueRef.current(JSON.parse(dataPayload) as QueueEntryApi[]);
      } catch {
        /* ligne non-JSON (keep-alive) — on ignore */
      }
    };

    const connect = async () => {
      try {
        const response = await fetch(`${config.API}${streamPath}`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            Accept: "text/event-stream",
          },
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`SSE status ${response.status}`);
        }

        retryDelayMs = 1000; // connexion OK → reset back-off
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!stopped) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let separatorIndex: number;
          // Les événements SSE sont séparés par une ligne vide (\n\n).
          while ((separatorIndex = buffer.indexOf("\n\n")) >= 0) {
            const rawEvent = buffer.slice(0, separatorIndex);
            buffer = buffer.slice(separatorIndex + 2);
            dispatchEvent(rawEvent);
          }
        }
      } catch (error) {
        if (controller.signal.aborted || stopped) return;
        // Reconnexion avec back-off.
        window.setTimeout(connect, retryDelayMs);
        retryDelayMs = Math.min(retryDelayMs * 2, 15000);
      }
    };

    connect();

    return () => {
      stopped = true;
      controller.abort();
    };
  }, [enabled, streamPath]);
}

export default useQueueStream;
